import { useEffect, useState } from 'react';
import { collection, doc, getDoc, onSnapshot, query, where } from 'firebase/firestore';
import { COLLECTIONS, paths } from '@mitang/shared';
import { auth, db } from '../lib/firebase';

/**
 * PRICE VISIBILITY (admin allow-list). Prices live in `product_prices`, which
 * security rules expose only to users with a `price_viewers/{uid}` doc. The app
 * simply asks: can I read my own allow-list entry? If yes, subscribe to the
 * category's price docs; if not, render exactly as before (no prices anywhere).
 */
export interface CategoryPrices {
  /** productId → price columns, in sheet order. */
  byProduct: Map<string, Array<{ k: string; v: string }>>;
  allowed: boolean;
}

const EMPTY: CategoryPrices = { byProduct: new Map(), allowed: false };

export function usePrices(category: string | null): CategoryPrices {
  const [allowed, setAllowed] = useState(false);
  const [byProduct, setByProduct] = useState<Map<string, Array<{ k: string; v: string }>>>(new Map());

  // Am I on the allow-list?
  useEffect(() => {
    const uid = auth.currentUser?.uid;
    if (!uid) {
      setAllowed(false);
      return;
    }
    let cancelled = false;
    void getDoc(doc(db, paths.priceViewer(uid)))
      .then((snap) => {
        if (!cancelled) setAllowed(snap.exists());
      })
      .catch(() => {
        if (!cancelled) setAllowed(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!allowed || !category) {
      setByProduct(new Map());
      return;
    }
    const q = query(collection(db, COLLECTIONS.productPrices), where('category', '==', category));
    const unsub = onSnapshot(
      q,
      (snap) => {
        const map = new Map<string, Array<{ k: string; v: string }>>();
        snap.forEach((d) => {
          const data = d.data() as { attrs?: Array<{ k: string; v: string }> };
          if (data.attrs?.length) map.set(d.id, data.attrs);
        });
        setByProduct(map);
      },
      () => setByProduct(new Map()),
    );
    return () => unsub();
  }, [allowed, category]);

  if (!allowed) return EMPTY;
  return { byProduct, allowed };
}

/** One product's prices (detail screen). */
export function useProductPrice(productId: string | null): Array<{ k: string; v: string }> | null {
  const [attrs, setAttrs] = useState<Array<{ k: string; v: string }> | null>(null);
  useEffect(() => {
    if (!productId) return;
    let cancelled = false;
    void getDoc(doc(db, paths.productPrice(productId)))
      .then((snap) => {
        if (cancelled) return;
        const data = snap.data() as { attrs?: Array<{ k: string; v: string }> } | undefined;
        setAttrs(data?.attrs?.length ? data.attrs : null);
      })
      .catch(() => {
        if (!cancelled) setAttrs(null); // not allow-listed → silently no prices
      });
    return () => {
      cancelled = true;
    };
  }, [productId]);
  return attrs;
}
